import React, { useState } from 'react';
import { DialogueLine, EntryTake } from '../../types';
import {
  Sliders,
  Mic,
  Volume2,
  CheckCircle2,
  History,
  Scissors,
  Pause,
  Play,
  Star,
  Trash2,
  GitMerge,
  ChevronDown,
  ChevronUp,
  Lock
} from 'lucide-react';

interface SelectedLineInspectorProps {
  dialogueLines: DialogueLine[];
  selectedLine: DialogueLine | null;
  selectedLineIds: Set<string>;
  takes: EntryTake[];
  duration: number;
  inspectorFocus: 'user' | 'orig';
  setInspectorFocus: (focus: 'user' | 'orig') => void;
  activeAuditionTakeId: string | null;
  isAuditioningOrigAudio: boolean;
  diarizeError: string | null;
  handlePrevLine: () => void;
  handleNextLine: () => void;
  handleSelectAllLines: () => void;
  handleDeselectAllLines: () => void;
  handleToggleLineSelection: (lineId: string, focus: 'user' | 'orig') => void;
  handleNudgeTakeTrim: (takeId: string, type: 'start' | 'end', delta: number) => void;
  handleNudgeTakeSync: (takeId: string, deltaSec: number) => void;
  handleUpdateTakeVolume: (takeId: string, volume: number) => void;
  handleAuditionTake: (take: EntryTake) => void;
  handleSetActiveTake: (lineId: string, takeId: string) => void;
  handleToggleActiveTake?: (lineId: string, takeId: string) => void;
  handleDeleteTake: (takeId: string) => void;
  handleAuditionSegment: (start: number, end: number) => void;
  handleNudgeTime: (lineId: string, type: 'start' | 'end', delta: number) => void;
  handleUpdateLineText: (lineId: string, text: string) => void;
  handleSplitLine: (lineId: string, splitPoint: number) => void;
  handleCombineWithNext: (lineId: string) => void;
  splitModeActive: boolean;
  setSplitModeActive: (active: boolean) => void;
  splitPoint: number;
  setSplitPoint: (point: number) => void;
}

export const SelectedLineInspector: React.FC<SelectedLineInspectorProps> = ({
  dialogueLines,
  selectedLine,
  selectedLineIds,
  takes,
  duration,
  inspectorFocus,
  setInspectorFocus,
  activeAuditionTakeId,
  isAuditioningOrigAudio,
  diarizeError,
  handlePrevLine,
  handleNextLine,
  handleSelectAllLines,
  handleDeselectAllLines,
  handleToggleLineSelection,
  handleNudgeTakeTrim,
  handleNudgeTakeSync,
  handleUpdateTakeVolume,
  handleAuditionTake,
  handleSetActiveTake,
  handleToggleActiveTake,
  handleDeleteTake,
  handleAuditionSegment,
  handleNudgeTime,
  handleUpdateLineText,
  handleSplitLine,
  handleCombineWithNext,
  splitModeActive,
  setSplitModeActive,
  splitPoint,
  setSplitPoint
}) => {
  const [expandedTakeIds, setExpandedTakeIds] = useState<Set<string>>(new Set());

  const toggleExpandTake = (takeId: string) => {
    setExpandedTakeIds((prev) => {
      const next = new Set(prev);
      if (next.has(takeId)) {
        next.delete(takeId);
      } else {
        next.add(takeId);
      }
      return next;
    });
  };

  return (
    <div className="studio-inspector-panel">
      <div className="studio-section-header" style={{ flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Sliders size={16} color="var(--accent-purple)" />
          <span style={{ fontSize: '1rem', fontWeight: 600, margin: 0, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '6px' }}>
            SoulScope
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* Prev / Next Line Navigation */}
          <button
            type="button"
            onClick={handlePrevLine}
            className="btn-secondary"
            disabled={dialogueLines.length === 0}
            style={{ fontSize: '0.75rem', padding: '0.35rem 0.65rem', display: 'flex', alignItems: 'center', gap: '4px' }}
            title="Previous line block"
          >
            ◀ Prev
          </button>
          <button
            type="button"
            onClick={handleNextLine}
            className="btn-secondary"
            disabled={dialogueLines.length === 0}
            style={{ fontSize: '0.75rem', padding: '0.35rem 0.65rem', display: 'flex', alignItems: 'center', gap: '4px' }}
            title="Next line block"
          >
            Next ▶
          </button>

          {/* Split Block Button */}
          {(() => {
            const hasTakesForSelected = selectedLine ? takes.some((t) => t.lineIds?.includes(selectedLine.id)) : false;
            const start = selectedLine ? (selectedLine.start ?? selectedLine.startTime ?? 0) : 0;
            const end = selectedLine ? (selectedLine.end ?? selectedLine.endTime ?? duration) : 0;
            const canSplit = !hasTakesForSelected && (end - start >= 1.0);
            return (
              <button
                type="button"
                onClick={() => {
                  if (selectedLine) {
                    setSplitPoint((start + end) / 2);
                  }
                  setSplitModeActive(!splitModeActive);
                }}
                disabled={!selectedLine || !canSplit}
                className="btn-secondary"
                style={{
                  fontSize: '0.75rem',
                  padding: '0.35rem 0.65rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: splitModeActive ? 'rgba(245, 158, 11, 0.25)' : undefined,
                  borderColor: splitModeActive ? 'rgba(245, 158, 11, 0.5)' : undefined
                }}
                title={!selectedLine ? 'Select a line first' : hasTakesForSelected ? 'Cannot split: line has takes' : end - start < 1.0 ? 'Line too short to split' : 'Split this block into two'}
              >
                <Scissors size={13} />
                Split
              </button>
            );
          })()}

          {/* Combine with Next Button */}
          {(() => {
            if (!selectedLine) return null;
            const selIdx = dialogueLines.findIndex((l) => l.id === selectedLine.id);
            const nextLine = dialogueLines[selIdx + 1];
            const hasTakesCurrent = takes.some((t) => t.lineIds?.includes(selectedLine.id));
            const hasTakesNext = nextLine ? takes.some((t) => t.lineIds?.includes(nextLine.id)) : false;
            const canCombine = nextLine && !hasTakesCurrent && !hasTakesNext;
            return (
              <button
                type="button"
                onClick={() => handleCombineWithNext(selectedLine.id)}
                disabled={!canCombine}
                className="btn-secondary"
                style={{ fontSize: '0.75rem', padding: '0.35rem 0.65rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                title={!nextLine ? 'No next line to combine with' : hasTakesCurrent ? 'Cannot combine: current line has takes' : hasTakesNext ? 'Cannot combine: next line has takes' : 'Merge this block with the next one'}
              >
                <GitMerge size={13} />
                Combine w/ Next
              </button>
            );
          })()}
        </div>
      </div>

      {/* Split Point Slider — visible when split mode is active */}
      {splitModeActive && selectedLine && (() => {
        const start = selectedLine.start ?? selectedLine.startTime ?? 0;
        const end = selectedLine.end ?? selectedLine.endTime ?? duration;
        const midpoint = (start + end) / 2;
        const canSplitBoth = (splitPoint - start >= 0.5) && (end - splitPoint >= 0.5);
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '0.75rem 1rem',
            background: 'rgba(245, 158, 11, 0.08)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            marginBottom: '0.75rem',
            flexWrap: 'wrap'
          }}>
            <Scissors size={14} color="#f59e0b" />
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fcd34d' }}>
              Split Point:
            </span>
            <input
              type="range"
              min={start}
              max={end}
              step={0.05}
              value={splitPoint || midpoint}
              onChange={(e) => setSplitPoint(Number(e.target.value))}
              style={{ flex: 1, minWidth: '100px', accentColor: '#f59e0b' }}
            />
            <span style={{ fontSize: '0.8rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#fcd34d', minWidth: '45px' }}>
              {(splitPoint || midpoint).toFixed(2)}s
            </span>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
              {((splitPoint || midpoint) - start).toFixed(2)}s | {((end - (splitPoint || midpoint)).toFixed(2))}s
            </span>
            <button
              type="button"
              onClick={() => {
                console.log('[Apply Split] clicked', 'selectedLine.id:', selectedLine?.id, 'splitPoint:', splitPoint || midpoint);
                handleSplitLine(selectedLine.id, splitPoint || midpoint);
                setSplitModeActive(false);
              }}
              disabled={!canSplitBoth}
              className="btn-primary"
              style={{
                padding: '0.3rem 0.75rem',
                fontSize: '0.75rem',
                background: canSplitBoth ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : undefined
              }}
            >
              Apply Split
            </button>
            <button
              type="button"
              onClick={() => setSplitModeActive(false)}
              className="btn-secondary"
              style={{ padding: '0.3rem 0.75rem', fontSize: '0.75rem' }}
            >
              Cancel
            </button>
          </div>
        );
      })()}

      {/* Diarization Error / API Key Banner */}
      {diarizeError && (
        <div style={{ padding: '0.75rem 1rem', background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.35)', borderRadius: 'var(--radius-md)', color: '#fcd34d', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', gap: '0.75rem' }}>
          <span>{diarizeError}</span>
        </div>
      )}

      {/* Dynamic Options Body */}
      {selectedLine ? (() => {
        const sIndex = dialogueLines.findIndex((l) => l.id === selectedLine.id);
        const start = selectedLine.start ?? selectedLine.startTime ?? 0;
        const end = selectedLine.end ?? selectedLine.endTime ?? duration;
        const lineDur = Math.max(0, end - start);
        const isSelectedForDub = selectedLineIds.has(selectedLine.id);
        const lineMid = (start + end) / 2;
        const lineTakes = takes
          .filter((t) => {
            if (t.lineIds && t.lineIds.length > 0) {
              return t.lineIds.includes(selectedLine.id);
            }
            return (
              t.lineStart !== undefined &&
              t.lineEnd !== undefined &&
              t.lineStart <= lineMid &&
              t.lineEnd >= lineMid
            );
          })
          .sort((a, b) => (b.takeNumber || 0) - (a.takeNumber || 0) || (b.createdAt || 0) - (a.createdAt || 0) || b.id.localeCompare(a.id));
        const activeTake = lineTakes.find((t) => t.isActive) || lineTakes[0];

        return (
          <div
            style={{
              background: 'rgba(15, 23, 42, 0.75)',
              border: inspectorFocus === 'user' ? '1.5px solid rgba(16, 185, 129, 0.4)' : '1.5px solid rgba(139, 92, 246, 0.35)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.25rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.2rem',
              boxShadow: inspectorFocus === 'user' ? '0 8px 32px rgba(16, 185, 129, 0.15)' : '0 8px 32px rgba(0, 0, 0, 0.3)',
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              boxSizing: 'border-box'
            }}
          >
            {/* Top Row: Line Index Badge, Spoken Dialogue in Bold, Dubbing Toggle */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', minWidth: 0, overflow: 'hidden', boxSizing: 'border-box', maxWidth: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
                <div
                  style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    background: inspectorFocus === 'user'
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'linear-gradient(135deg, var(--accent-purple) 0%, #7c3aed 100%)',
                    color: '#fff',
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    boxShadow: inspectorFocus === 'user' ? '0 0 12px rgba(16, 185, 129, 0.4)' : '0 0 12px rgba(168, 85, 247, 0.4)',
                    flexShrink: 0
                  }}
                >
                  LINE #{sIndex + 1} {inspectorFocus === 'user' ? '• USER TRACK' : '• ORIG TRACK'}
                </div>

                <div style={{ flex: 1, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                  <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#ffffff', lineHeight: 1.4, letterSpacing: '0.01em', marginBottom: '4px', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%', boxSizing: 'border-box' }}>
                    {selectedLine.words && selectedLine.words.length > 0 ? (
                      <span>
                        "{selectedLine.words.map((w, idx) => (
                          <span
                            key={idx}
                            title={`${w.start.toFixed(2)}s - ${w.end.toFixed(2)}s`}
                            style={{ display: 'inline-block', marginRight: '0.28em' }}
                          >
                            {w.word}
                          </span>
                        ))}"
                      </span>
                    ) : (
                      `"${selectedLine.text || selectedLine.originalText || `Dialogue Sentence #${sIndex + 1}`}"`
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Range: <strong style={{ color: '#38bdf8' }}>{start.toFixed(2)}s</strong> to <strong style={{ color: '#38bdf8' }}>{end.toFixed(2)}s</strong> ({lineDur.toFixed(2)}s)
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {/* Layer View Mode Switcher */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(0, 0, 0, 0.4)', padding: '3px', borderRadius: '6px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <button
                    type="button"
                    onClick={() => setInspectorFocus('user')}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '4px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      background: inspectorFocus === 'user' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'transparent',
                      color: inspectorFocus === 'user' ? '#ffffff' : 'var(--text-muted)',
                      transition: 'all 0.15s ease'
                    }}
                    title="Inspect user voice takes for this range"
                  >
                    <Mic size={13} />
                    <span>User Voice ({lineTakes.length})</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setInspectorFocus('orig')}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '4px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      background: inspectorFocus === 'orig' ? 'linear-gradient(135deg, var(--accent-purple) 0%, #7c3aed 100%)' : 'transparent',
                      color: inspectorFocus === 'orig' ? '#ffffff' : 'var(--text-muted)',
                      transition: 'all 0.15s ease'
                    }}
                    title="Inspect original voice timing & script"
                  >
                    <Volume2 size={13} />
                    <span>Orig Voice</span>
                  </button>
                </div>

                {/* Armed Status Badge (Arming constrained to Teleprompter window) */}
                <div
                  style={{
                    background: isSelectedForDub
                      ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.25) 0%, rgba(99, 102, 241, 0.2) 100%)'
                      : 'rgba(255, 255, 255, 0.04)',
                    border: isSelectedForDub
                      ? '1px solid rgba(168, 85, 247, 0.6)'
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: isSelectedForDub ? '0 0 12px rgba(168, 85, 247, 0.3)' : 'none',
                    color: isSelectedForDub ? '#f3e8ff' : 'var(--text-muted)',
                    fontSize: '0.8rem',
                    padding: '0.45rem 0.85rem',
                    borderRadius: '6px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  title="Arm or disarm lines by clicking them in the teleprompter window"
                >
                  {isSelectedForDub ? (
                    <>
                      <CheckCircle2 size={15} color="#c084fc" />
                      <span>● Selected to Record</span>
                    </>
                  ) : (
                    <>
                      <Mic size={15} color="var(--text-muted)" />
                      <span>○ Not Selected (Click line in Teleprompter)</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ─────────────────────────────────────────────────── */}
            {/* VIEW A: USER VOICE TRACK & PREVIOUS TAKES INSPECTOR */}
            {/* ─────────────────────────────────────────────────── */}
            {inspectorFocus === 'user' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Takes Section Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '0.5rem', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <History size={16} color="#34d399" />
                    <span style={{ fontSize: '0.82rem', fontWeight: 800, textTransform: 'uppercase', color: '#e2e8f0', letterSpacing: '0.04em' }}>
                      Recorded Takes ({lineTakes.length})
                    </span>
                  </div>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    Active take is played in studio mix. Audition, nudge sync (±50ms), or switch active take below.
                  </span>
                </div>

                {/* Takes List */}
                {lineTakes.length === 0 ? (
                  <div
                    style={{
                      padding: '1.75rem',
                      textAlign: 'center',
                      background: 'rgba(0, 0, 0, 0.25)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px dashed rgba(255, 255, 255, 0.15)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Mic size={24} color="#10b981" style={{ opacity: 0.8 }} />
                    <div style={{ fontSize: '0.85rem', color: '#e2e8f0', fontWeight: 600 }}>
                      No user takes recorded yet for Line #{sIndex + 1}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', maxWidth: '420px' }}>
                      This dub slot is ready! Press Record in Section 3 to lay down your voice over this {lineDur.toFixed(1)}s segment.
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAuditionSegment(start, end)}
                      className="btn-secondary"
                      style={{ marginTop: '6px', fontSize: '0.75rem', padding: '0.4rem 0.8rem', display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                      <Volume2 size={13} />
                      <span>Audition Original Guide Vocal ({start.toFixed(1)}s - {end.toFixed(1)}s)</span>
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {lineTakes.map((take, tIdx) => {
                      const isThisActive = activeTake?.id === take.id || !!take.isActive;
                      const isExpanded = isThisActive || expandedTakeIds.has(take.id);
                      const isAuditioningThis = activeAuditionTakeId === take.id;
                      const durationSec = take.duration ? take.duration : Math.max(0, take.lineEnd - take.lineStart);

                      // Inactive takes: twirled up by default featuring playback button and length info
                      if (!isExpanded) {
                        return (
                          <div
                            key={take.id || tIdx}
                            style={{
                              background: 'rgba(255, 255, 255, 0.025)',
                              border: '1px solid rgba(255, 255, 255, 0.08)',
                              borderRadius: 'var(--radius-md)',
                              padding: '0.5rem 0.85rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '12px',
                              flexWrap: 'wrap',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            {/* Left: Take Index & Length Info */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div
                                style={{
                                  width: '26px',
                                  height: '26px',
                                  borderRadius: '5px',
                                  background: 'rgba(255, 255, 255, 0.07)',
                                  color: 'rgba(255, 255, 255, 0.85)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 700,
                                  fontSize: '0.75rem',
                                  flexShrink: 0
                                }}
                              >
                                #{take.takeNumber || tIdx + 1}
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'rgba(255, 255, 255, 0.9)' }}>
                                  Take #{take.takeNumber || tIdx + 1}
                                </span>
                                <span
                                  style={{
                                    fontSize: '0.72rem',
                                    fontFamily: 'var(--font-mono)',
                                    color: '#34d399',
                                    background: 'rgba(16, 185, 129, 0.12)',
                                    border: '1px solid rgba(16, 185, 129, 0.25)',
                                    padding: '1px 6px',
                                    borderRadius: '4px',
                                    fontWeight: 700
                                  }}
                                >
                                  {durationSec.toFixed(2)}s
                                </span>
                                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                  ({take.lineStart.toFixed(2)}s – {take.lineEnd.toFixed(2)}s)
                                </span>
                                <span style={{ fontSize: '0.7rem', color: 'rgba(148, 163, 184, 0.6)' }}>
                                  • By {take.performerName || 'You'}
                                </span>
                              </div>
                            </div>

                            {/* Right: Playback, Set as Active, Twirl Toggle, Delete */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {/* Audition / Playback Button */}
                              <button
                                type="button"
                                onClick={() => handleAuditionTake(take)}
                                className="btn-secondary"
                                style={{
                                  padding: '3px 9px',
                                  fontSize: '0.75rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  background: isAuditioningThis
                                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                    : 'rgba(255, 255, 255, 0.08)',
                                  borderColor: isAuditioningThis ? '#f59e0b' : 'rgba(255, 255, 255, 0.15)',
                                  color: '#ffffff'
                                }}
                                title={isAuditioningThis ? 'Pause audition' : 'Audition this take'}
                              >
                                {isAuditioningThis ? <Pause size={12} /> : <Play size={12} fill="currentColor" />}
                                <span>{isAuditioningThis ? 'Pause' : 'Audition'}</span>
                              </button>

                              {/* Set as Active Take */}
                              <button
                                type="button"
                                onClick={() => handleSetActiveTake(selectedLine.id, take.id)}
                                className="btn-secondary"
                                style={{ padding: '3px 8px', fontSize: '0.72rem', color: '#34d399', borderColor: 'rgba(16, 185, 129, 0.35)' }}
                                title="Use this take for playback & export"
                              >
                                Set Active
                              </button>

                              {/* Twirl down controls */}
                              <button
                                type="button"
                                onClick={() => toggleExpandTake(take.id)}
                                className="btn-secondary"
                                style={{ padding: '3px 6px', fontSize: '0.72rem', color: 'var(--text-muted)' }}
                                title="Twirl down controls (volume, trim, slip)"
                              >
                                <ChevronDown size={13} />
                              </button>

                              {/* Delete take button */}
                              <button
                                type="button"
                                onClick={() => handleDeleteTake(take.id)}
                                className="btn-secondary"
                                style={{ padding: '3px 7px', fontSize: '0.75rem', color: '#f87171', borderColor: 'rgba(239, 68, 68, 0.25)' }}
                                title="Delete this take"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                        );
                      }

                      // Active take (or manually expanded take): full controls (volume, trim, slip)
                      return (
                        <div
                          key={take.id || tIdx}
                          style={{
                            background: isThisActive ? 'rgba(16, 185, 129, 0.12)' : 'rgba(255, 255, 255, 0.03)',
                            border: isThisActive ? '1.5px solid #10b981' : '1px solid rgba(255, 255, 255, 0.1)',
                            boxShadow: isThisActive ? '0 0 16px rgba(16, 185, 129, 0.2)' : 'none',
                            borderRadius: 'var(--radius-md)',
                            padding: '0.85rem 1rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                            flexWrap: 'wrap',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {/* Left: Take Index, Badges, Details */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '220px' }}>
                            <div
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px',
                                background: isThisActive ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'rgba(255, 255, 255, 0.08)',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 800,
                                fontSize: '0.8rem',
                                flexShrink: 0
                              }}
                            >
                              #{take.takeNumber || tIdx + 1}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#ffffff' }}>
                                  Take #{take.takeNumber || tIdx + 1}
                                </span>
                                {isThisActive ? (
                                  <span
                                    className="badge"
                                    style={{
                                      background: 'rgba(16, 185, 129, 0.25)',
                                      color: '#34d399',
                                      border: '1px solid #10b981',
                                      fontSize: '9px',
                                      padding: '1px 6px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '3px'
                                    }}
                                  >
                                    <Star size={9} fill="currentColor" />
                                    ACTIVE TAKE
                                  </span>
                                ) : (
                                  <span style={{ fontSize: '9px', color: 'var(--text-muted)', background: 'rgba(255, 255, 255, 0.06)', padding: '1px 5px', borderRadius: '3px' }}>
                                    Previous Take
                                  </span>
                                )}
                              </div>
                              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                By {take.performerName || 'You'} • {durationSec.toFixed(2)}s duration{take.lineStart < 0 ? ' (clipped before 0s)' : ''}
                              </span>
                            </div>
                          </div>

                          {/* Middle: Take Sync & Trim In/Out Controls */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {/* Take Slip / Position Sync Nudge */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(0, 0, 0, 0.35)', padding: '4px 8px', borderRadius: '6px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                              <span style={{ fontSize: '0.72rem', color: '#6ee7b7', fontWeight: 700 }}>Slip:</span>
                              <button
                                type="button"
                                onClick={() => handleNudgeTakeSync(take.id, -0.05)}
                                style={{ padding: '2px 6px', background: 'rgba(255, 255, 255, 0.12)', borderRadius: '3px', color: '#fff', fontSize: '11px', cursor: 'pointer', border: 'none' }}
                                title="Slip entire take earlier by 50ms (-0.05s)"
                              >
                                ◀ -50ms
                              </button>
                              <span
                                style={{
                                  fontSize: '0.8rem',
                                  fontFamily: 'var(--font-mono)',
                                  fontWeight: 700,
                                  color: take.lineStart < 0 ? '#fcd34d' : '#ffffff',
                                  minWidth: '50px',
                                  textAlign: 'center'
                                }}
                                title={take.lineStart < 0 ? `Slipped before 0.00s (${take.lineStart.toFixed(2)}s) — clipped at time 0` : undefined}
                              >
                                {take.lineStart.toFixed(2)}s
                              </span>
                              <button
                                type="button"
                                onClick={() => handleNudgeTakeSync(take.id, 0.05)}
                                style={{ padding: '2px 6px', background: 'rgba(255, 255, 255, 0.12)', borderRadius: '3px', color: '#fff', fontSize: '11px', cursor: 'pointer', border: 'none' }}
                                title="Slip entire take later by 50ms (+0.05s)"
                              >
                                +50ms ▶
                              </button>
                            </div>

                            {/* Take Trim In Point (equivalent to dragging green block left handle) */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(0, 0, 0, 0.35)', padding: '4px 8px', borderRadius: '6px', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
                              <span style={{ fontSize: '0.72rem', color: '#34d399', fontWeight: 700 }}>Trim In:</span>
                              <button
                                type="button"
                                onClick={() => handleNudgeTakeTrim(take.id, 'start', -0.05)}
                                style={{ padding: '2px 6px', background: 'rgba(255, 255, 255, 0.12)', borderRadius: '3px', color: '#fff', fontSize: '11px', cursor: 'pointer', border: 'none' }}
                                title="Expand In-Point earlier by 50ms (-0.05s)"
                              >
                                -
                              </button>
                              <span
                                style={{
                                  fontSize: '0.8rem',
                                  fontFamily: 'var(--font-mono)',
                                  fontWeight: 700,
                                  color: '#6ee7b7',
                                  minWidth: '46px',
                                  textAlign: 'center'
                                }}
                              >
                                {take.lineStart.toFixed(2)}s
                              </span>
                              <button
                                type="button"
                                onClick={() => handleNudgeTakeTrim(take.id, 'start', 0.05)}
                                style={{ padding: '2px 6px', background: 'rgba(255, 255, 255, 0.12)', borderRadius: '3px', color: '#fff', fontSize: '11px', cursor: 'pointer', border: 'none' }}
                                title="Trim In-Point later by 50ms (+0.05s)"
                              >
                                +
                              </button>
                            </div>

                            {/* Take Trim Out Point (equivalent to dragging green block right handle) */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(0, 0, 0, 0.35)', padding: '4px 8px', borderRadius: '6px', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
                              <span style={{ fontSize: '0.72rem', color: '#34d399', fontWeight: 700 }}>Trim Out:</span>
                              <button
                                type="button"
                                onClick={() => handleNudgeTakeTrim(take.id, 'end', -0.05)}
                                style={{ padding: '2px 6px', background: 'rgba(255, 255, 255, 0.12)', borderRadius: '3px', color: '#fff', fontSize: '11px', cursor: 'pointer', border: 'none' }}
                                title="Trim Out-Point earlier by 50ms (-0.05s)"
                              >
                                -
                              </button>
                              <span
                                style={{
                                  fontSize: '0.8rem',
                                  fontFamily: 'var(--font-mono)',
                                  fontWeight: 700,
                                  color: '#6ee7b7',
                                  minWidth: '46px',
                                  textAlign: 'center'
                                }}
                              >
                                {take.lineEnd.toFixed(2)}s
                              </span>
                              <button
                                type="button"
                                onClick={() => handleNudgeTakeTrim(take.id, 'end', 0.05)}
                                style={{ padding: '2px 6px', background: 'rgba(255, 255, 255, 0.12)', borderRadius: '3px', color: '#fff', fontSize: '11px', cursor: 'pointer', border: 'none' }}
                                title="Extend Out-Point later by 50ms (+0.05s)"
                              >
                                +
                              </button>
                            </div>
                          </div>

                          {/* Take Volume Slider */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(0, 0, 0, 0.35)', padding: '4px 10px', borderRadius: '6px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                            <Volume2 size={13} color="#a78bfa" />
                            <span style={{ fontSize: '0.72rem', color: '#c4b5fd', fontWeight: 700 }}>Vol:</span>
                            <input
                              type="range"
                              min="0"
                              max="150"
                              step="5"
                              value={Math.round((take.volume ?? 1.0) * 100)}
                              onChange={(e) => handleUpdateTakeVolume(take.id, Number(e.target.value) / 100)}
                              style={{ width: '75px', height: '4px', accentColor: 'var(--accent-purple)', cursor: 'pointer' }}
                              title={`Adjust playback volume for Take #${take.takeNumber || tIdx + 1} (${Math.round((take.volume ?? 1.0) * 100)}%)`}
                            />
                            <span style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#ffffff', minWidth: '38px', textAlign: 'right' }}>
                              {Math.round((take.volume ?? 1.0) * 100)}%
                            </span>
                          </div>

                          {/* Right: Audition, Set Active, Twirl Up, Delete */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {/* Audition Button */}
                            <button
                              type="button"
                              onClick={() => handleAuditionTake(take)}
                              className="btn-primary"
                              style={{
                                padding: '4px 10px',
                                fontSize: '0.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                background: isAuditioningThis
                                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                              }}
                              title={isAuditioningThis ? 'Stop auditioning' : 'Audition this take'}
                            >
                              {isAuditioningThis ? <Pause size={12} /> : <Play size={12} fill="currentColor" />}
                              <span>{isAuditioningThis ? 'Pause' : 'Audition'}</span>
                            </button>

                            {/* Partner locked take in Colab mode */}
                            {take.isLocked ? (
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  fontSize: '0.74rem',
                                  fontWeight: 700,
                                  color: '#f59e0b',
                                  background: 'rgba(245, 158, 11, 0.15)',
                                  border: '1px solid rgba(245, 158, 11, 0.35)',
                                  borderRadius: '6px',
                                  padding: '3px 8px'
                                }}
                                title="Partner take locked in Colab mode"
                              >
                                <Lock size={12} />
                                <span>@{take.performerName || 'Partner'} (Locked)</span>
                              </span>
                            ) : (
                              <>
                                {/* Toggle Active / Deactivate Take */}
                                <button
                                  type="button"
                                  onClick={() => (handleToggleActiveTake || handleSetActiveTake)(selectedLine.id, take.id)}
                                  className="btn-secondary"
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '0.72rem',
                                    color: isThisActive ? '#f87171' : '#34d399',
                                    borderColor: isThisActive ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)'
                                  }}
                                  title={isThisActive ? 'Deactivate take to restore original scene vocal' : 'Use this take for playback & export'}
                                >
                                  {isThisActive ? 'Deactivate (Clean Vocal)' : 'Set as Active'}
                                </button>

                                {/* Twirl up button (if manually expanded inactive take) */}
                                {!isThisActive && (
                                  <button
                                    type="button"
                                    onClick={() => toggleExpandTake(take.id)}
                                    className="btn-secondary"
                                    style={{ padding: '4px 6px', fontSize: '0.72rem', color: 'var(--text-muted)' }}
                                    title="Twirl up controls"
                                  >
                                    <ChevronUp size={13} />
                                  </button>
                                )}

                                {/* Delete take button */}
                                <button
                                  type="button"
                                  onClick={() => handleDeleteTake(take.id)}
                                  className="btn-secondary"
                                  style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#f87171', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                                  title="Delete this take"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Companion Audition Original Guide Vocal */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.9rem', background: 'rgba(0, 0, 0, 0.2)', borderRadius: 'var(--radius-sm)', marginTop: '4px', flexWrap: 'wrap', gap: '8px' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        Need to compare timing or delivery against the original line?
                      </span>
                      <button
                        type="button"
                        onClick={() => handleAuditionSegment(start, end)}
                        className="btn-secondary"
                        style={{ fontSize: '0.75rem', padding: '3px 8px', display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        <Volume2 size={12} />
                        <span>Audition Original Vocal ({start.toFixed(1)}s - {end.toFixed(1)}s)</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* ─────────────────────────────────────────────────── */
              /* VIEW B: ORIGINAL VOICE TRACK & TIMING FINE-TUNING  */
              /* ─────────────────────────────────────────────────── */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '12px',
                    background: 'rgba(0, 0, 0, 0.25)',
                    padding: '1rem',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(255, 255, 255, 0.06)'
                  }}
                >
                  {/* Timing Fine-Tuning Box */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>
                      Original Dialogue Block Range
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      {/* Start Time Nudge */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(255, 255, 255, 0.05)', padding: '4px 8px', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>In:</span>
                        <button
                          type="button"
                          onClick={() => handleNudgeTime(selectedLine.id, 'start', -0.1)}
                          style={{ padding: '1px 6px', background: 'rgba(255, 255, 255, 0.12)', borderRadius: '3px', color: '#fff', fontSize: '11px', cursor: 'pointer', border: 'none' }}
                          title="Nudge In-Point earlier (-0.1s)"
                        >
                          -
                        </button>
                        <span style={{ fontSize: '0.82rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#38bdf8' }}>{start.toFixed(2)}s</span>
                        <button
                          type="button"
                          onClick={() => handleNudgeTime(selectedLine.id, 'start', 0.1)}
                          style={{ padding: '1px 6px', background: 'rgba(255, 255, 255, 0.12)', borderRadius: '3px', color: '#fff', fontSize: '11px', cursor: 'pointer', border: 'none' }}
                          title="Nudge In-Point later (+0.1s)"
                        >
                          +
                        </button>
                      </div>

                      {/* End Time Nudge */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(255, 255, 255, 0.05)', padding: '4px 8px', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Out:</span>
                        <button
                          type="button"
                          onClick={() => handleNudgeTime(selectedLine.id, 'end', -0.1)}
                          style={{ padding: '1px 6px', background: 'rgba(255, 255, 255, 0.12)', borderRadius: '3px', color: '#fff', fontSize: '11px', cursor: 'pointer', border: 'none' }}
                          title="Nudge Out-Point earlier (-0.1s)"
                        >
                          -
                        </button>
                        <span style={{ fontSize: '0.82rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#38bdf8' }}>{end.toFixed(2)}s</span>
                        <button
                          type="button"
                          onClick={() => handleNudgeTime(selectedLine.id, 'end', 0.1)}
                          style={{ padding: '1px 6px', background: 'rgba(255, 255, 255, 0.12)', borderRadius: '3px', color: '#fff', fontSize: '11px', cursor: 'pointer', border: 'none' }}
                          title="Nudge Out-Point later (+0.1s)"
                        >
                          +
                        </button>
                      </div>

                      {/* Audition Original */}
                      <button
                        type="button"
                        onClick={() => handleAuditionSegment(start, end)}
                        className={isAuditioningOrigAudio ? "btn-primary" : "btn-secondary"}
                        style={{
                          padding: '4px 10px',
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          background: isAuditioningOrigAudio ? 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' : undefined
                        }}
                        title={isAuditioningOrigAudio ? "Stop auditioning original vocal" : "Audition original guide vocal for this block (±2s padding)"}
                      >
                        <Volume2 size={13} />
                        <span>{isAuditioningOrigAudio ? 'Auditioning...' : 'Audition Orig'}</span>
                      </button>
                    </div>
                  </div>

                  {/* Quick Link to User Takes */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', justifyContent: 'center' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>
                      User Voice Status
                    </span>
                    {lineTakes.length > 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', background: 'rgba(16, 185, 129, 0.1)', padding: '6px 10px', borderRadius: '4px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                        <span style={{ fontSize: '0.75rem', color: '#6ee7b7' }}>
                          ✓ {lineTakes.length} take(s) recorded
                        </span>
                        <button
                          type="button"
                          onClick={() => setInspectorFocus('user')}
                          className="btn-primary"
                          style={{ fontSize: '0.72rem', padding: '3px 8px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                        >
                          View Takes →
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', background: 'rgba(255, 255, 255, 0.03)', padding: '6px 10px', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          No takes recorded yet
                        </span>
                        <button
                          type="button"
                          onClick={() => setInspectorFocus('user')}
                          className="btn-secondary"
                          style={{ fontSize: '0.72rem', padding: '3px 8px' }}
                        >
                          Open Dub Slot →
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Editable line text for script tweaking */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Edit transcript text (optional):</span>
                  <input
                    type="text"
                    value={selectedLine.text || ''}
                    onChange={(e) => handleUpdateLineText(selectedLine.id, e.target.value)}
                    placeholder="Type spoken sentence text..."
                    style={{
                      width: '100%',
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      borderRadius: 'var(--radius-sm)',
                      color: '#ffffff',
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.85rem'
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })() : (
        <div
          style={{
            padding: '2rem',
            textAlign: 'center',
            background: 'rgba(15, 23, 42, 0.4)',
            borderRadius: 'var(--radius-md)',
            border: '1px dashed rgba(255, 255, 255, 0.15)',
            color: 'var(--text-muted)',
            fontSize: '0.85rem'
          }}
        >
          Click any block on Layer 1 (User Voice) or Layer 2 (Original Voice) of the timeline above to inspect its details and takes.
        </div>
      )}
    </div>
  );
};
